type Pipeline = (input: unknown, options?: Record<string, unknown>) => Promise<unknown>;

let whisperPromise: Promise<Pipeline> | null = null;
let kokoroPromise: Promise<Pipeline> | null = null;

const createPipeline = async (task: string, model: string): Promise<Pipeline> => {
  const transformers = await import("@huggingface/transformers");
  transformers.env.allowLocalModels = false;
  const factory = transformers.pipeline as unknown as (
    task: string,
    model: string,
    options: Record<string, unknown>,
  ) => Promise<Pipeline>;
  const device = "gpu" in navigator ? "webgpu" : "wasm";
  return factory(task, model, { device, dtype: "q8" });
};

const resample = (input: Float32Array, fromRate: number, toRate = 16_000) => {
  if (fromRate === toRate) return input;
  const output = new Float32Array(Math.round((input.length * toRate) / fromRate));
  const ratio = fromRate / toRate;
  for (let index = 0; index < output.length; index += 1) {
    const position = index * ratio;
    const left = Math.floor(position);
    const right = Math.min(left + 1, input.length - 1);
    const mix = position - left;
    output[index] = input[left] * (1 - mix) + input[right] * mix;
  }
  return output;
};

export async function transcribeWithWhisper(blob: Blob) {
  whisperPromise ??= createPipeline("automatic-speech-recognition", "onnx-community/whisper-tiny.en");
  const context = new AudioContext();
  try {
    const decoded = await context.decodeAudioData(await blob.arrayBuffer());
    const audio = resample(decoded.getChannelData(0), decoded.sampleRate);
    const result = (await (await whisperPromise)(audio)) as { text?: string };
    return result.text?.trim() ?? "";
  } finally {
    await context.close();
  }
}

const deviceVoiceFallback = (text: string) =>
  new Promise<void>((resolve, reject) => {
    if (!("speechSynthesis" in window)) {
      reject(new Error("Speech is unavailable on this device."));
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.pitch = 1.18;
    utterance.rate = 1.02;
    utterance.onend = () => resolve();
    utterance.onerror = () => reject(new Error("Device voice failed."));
    speechSynthesis.cancel();
    speechSynthesis.speak(utterance);
  });

export async function speakWithKokoro(text: string, onState?: (speaking: boolean) => void) {
  onState?.(true);
  try {
    kokoroPromise ??= createPipeline("text-to-speech", "onnx-community/Kokoro-82M-v1.0-ONNX");
    const result = (await (await kokoroPromise)(text, { voice: "af_heart" })) as {
      audio?: Float32Array;
      sampling_rate?: number;
      samplingRate?: number;
    };
    if (!result.audio) throw new Error("Kokoro returned no audio.");
    const context = new AudioContext();
    const buffer = context.createBuffer(1, result.audio.length, result.sampling_rate ?? result.samplingRate ?? 24_000);
    buffer.copyToChannel(new Float32Array(result.audio), 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    await new Promise<void>((resolve) => {
      source.onended = () => resolve();
      source.start();
    });
    await context.close();
  } catch {
    await deviceVoiceFallback(text);
  } finally {
    onState?.(false);
  }
}
